import type { PolicySetting } from '@kirin/api-client/contracts/policies';
import { SPECIAL_SETTINGS, POLICY_KEYS } from '../constants';

/**
 * Extracts enum values from a validation pattern
 * Supports patterns like: ^(low|medium|high)$
 * Returns array of enum values or null if not an enum pattern
 *
 * @param pattern - The regex pattern to extract enum values from
 * @returns Array of enum values or null if pattern doesn't match enum format
 */
export function extractEnumValues(pattern: string): string[] | null {
  // Match patterns like: ^(value1|value2|value3)$
  const enumMatch = pattern.match(/^\^\(([^)]+)\)\$$/);
  if (!enumMatch) return null;

  const values = enumMatch[1].split('|').map(v => v.trim());
  return values.length > 1 ? values : null;
}

/**
 * Checks if a string setting should render as a select dropdown
 * Based on validation pattern matching enum format
 *
 * @param setting - The policy setting to check
 * @returns True if the setting should render as a select dropdown
 */
export function shouldRenderAsSelect(setting: PolicySetting): boolean {
  // Check if pattern matches enum format
  if (setting.validation?.pattern) {
    const enumValues = extractEnumValues(setting.validation.pattern);
    if (enumValues && enumValues.length > 0) {
      return true;
    }
  }

  // Fallback: Check known enum fields
  if (setting.key === SPECIAL_SETTINGS.AI_CODE_SENSITIVITY) {
    return true;
  }

  return false;
}

/**
 * Gets enum options from a setting's validation pattern
 * Falls back to known constants if pattern parsing fails
 *
 * @param setting - The policy setting to extract enum options from
 * @returns Array of {value, label} objects for select dropdown
 */
export function getEnumOptions(
  setting: PolicySetting
): Array<{ value: string; label: string }> {
  // Try to extract from pattern
  if (setting.validation?.pattern) {
    const enumValues = extractEnumValues(setting.validation.pattern);
    if (enumValues) {
      return enumValues.map(value => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
      }));
    }
  }

  // Fallback: Known enum fields
  if (setting.key === SPECIAL_SETTINGS.AI_CODE_SENSITIVITY) {
    return [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
    ];
  }

  return [];
}

/**
 * Checks if an array object setting has a permission field
 * This indicates it should use ArrayObjectSetting (extension-security style)
 */
function hasPermissionField(properties?: Record<string, unknown>): boolean {
  if (!properties) return false;

  const permissionProp = properties.permission;
  if (typeof permissionProp === 'object' && permissionProp !== null) {
    const prop = permissionProp as { type?: string; required?: boolean };
    return prop.type === 'string';
  }

  return false;
}

/**
 * Checks if an array object setting has a required pattern field
 * This indicates it should use PatternArrayInput (pattern-based style)
 */
function hasRequiredPatternField(
  properties?: Record<string, unknown>
): boolean {
  if (!properties) return false;

  const patternProp = properties.pattern;
  if (typeof patternProp === 'object' && patternProp !== null) {
    const prop = patternProp as { type?: string; required?: boolean };
    return prop.type === 'string' && prop.required === true;
  }

  return false;
}

/**
 * Determines which array component to use based on validation properties
 * Returns 'array-object' for extension-security style, 'array-pattern' for pattern-based
 *
 * @param setting - The policy setting configuration
 * @param policyKey - The policy key for fallback component type detection
 * @returns 'array-object' or 'array-pattern' indicating which component to use
 */
export function getArrayObjectComponentType(
  setting: PolicySetting,
  policyKey: string
): 'array-object' | 'array-pattern' {
  // First, try to derive from validation.properties structure
  if (setting.validation?.properties) {
    const hasPermission = hasPermissionField(setting.validation.properties);
    const hasPattern = hasRequiredPatternField(setting.validation.properties);

    // If has permission but no required pattern, it's extension-security style
    if (hasPermission && !hasPattern) {
      return 'array-object';
    }

    // If has required pattern, it's pattern-based style
    if (hasPattern) {
      return 'array-pattern';
    }
  }

  // Fallback: Check known policy keys
  if (policyKey === POLICY_KEYS.EXTENSION_SECURITY) {
    return 'array-object';
  }

  // Default: pattern-based (most common)
  return 'array-pattern';
}
